import React from "react";
import { SEOHead } from "@/components/SEOHead";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <>
      <SEOHead
        title="Privacy Policy | DayOne"
        description="DayOne privacy policy — how we collect, use, and protect your data. GDPR and CCPA compliant."
      />
      <div className="min-h-screen lavender-bg" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div className="max-w-[800px] mx-auto px-6 pt-16 pb-24">
          {/* Header */}
          <p className="text-xs font-medium tracking-widest uppercase mb-4" style={{ color: "#7c6af0" }}>
            Privacy Policy
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif", color: "#18151f" }}>
            Privacy Policy
          </h1>
          <p className="text-sm mb-1" style={{ color: "#6b5f8a" }}>
            DayOne — usedayone.net
          </p>
          <p className="text-sm mb-12" style={{ color: "#6b5f8a" }}>
            Effective Date: April 24, 2026 | Last Updated: April 24, 2026
          </p>

          <div className="space-y-10 text-[15px] leading-[1.75]" style={{ color: "#6b5f8a" }}>
            {/* 1 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>1. Introduction</h2>
              <p>
                DayOne ("we", "us", or "our") operates usedayone.net (the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Service. By using DayOne, you agree to the practices described in this policy. We are committed to protecting your privacy and complying with the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable data protection laws.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>2. Information We Collect</h2>

              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: "#18151f" }}>2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong style={{ color: "#18151f" }}>Account information:</strong> name, email address, and profile details when you create an account</li>
                <li><strong style={{ color: "#18151f" }}>Google OAuth data:</strong> if you sign in with Google, we receive your name, email, and profile picture from Google</li>
                <li><strong style={{ color: "#18151f" }}>Content you create:</strong> app prompts, saved ideas, and projects you build on DayOne</li>
                <li><strong style={{ color: "#18151f" }}>Communications:</strong> messages you send to our support team</li>
              </ul>

              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: "#18151f" }}>2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong style={{ color: "#18151f" }}>Usage data:</strong> pages visited, features used, time spent, and actions taken in the app</li>
                <li><strong style={{ color: "#18151f" }}>Device information:</strong> browser type, operating system, and IP address</li>
                <li><strong style={{ color: "#18151f" }}>Analytics data:</strong> via PostHog and Plausible analytics (privacy-focused, no cross-site tracking)</li>
                <li><strong style={{ color: "#18151f" }}>Build activity:</strong> prompts sent to the AI builder and apps you create</li>
              </ul>

              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: "#18151f" }}>2.3 Information from Third Parties</h3>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong style={{ color: "#18151f" }}>Google:</strong> authentication data when you use Google Sign-In</li>
                <li><strong style={{ color: "#18151f" }}>Vercel:</strong> deployment data when you publish apps</li>
                <li><strong style={{ color: "#18151f" }}>Supabase:</strong> database and authentication infrastructure</li>
              </ul>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>3. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Provide, maintain, and improve the DayOne platform</li>
                <li>Process your requests and generate AI-powered applications</li>
                <li>Authenticate your identity and secure your account</li>
                <li>Send transactional emails (account confirmations, password resets)</li>
                <li>Analyze usage patterns to improve our Service</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraud and abuse</li>
              </ul>
              <p className="mt-3" style={{ color: "#18151f" }}>
                We do not sell your personal data to third parties. We do not use your data to train AI models without your explicit consent.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>4. Legal Basis for Processing (GDPR)</h2>
              <p className="mb-3">For users in the European Economic Area, we process your data under the following legal bases:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong style={{ color: "#18151f" }}>Contract performance:</strong> processing necessary to provide the Service you signed up for</li>
                <li><strong style={{ color: "#18151f" }}>Legitimate interests:</strong> analytics and security to improve and protect our Service</li>
                <li><strong style={{ color: "#18151f" }}>Legal obligation:</strong> compliance with applicable laws</li>
                <li><strong style={{ color: "#18151f" }}>Consent:</strong> where you have explicitly agreed to specific processing</li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>5. Data Sharing and Disclosure</h2>
              <p className="mb-3">We share your data only in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong style={{ color: "#18151f" }}>Service providers:</strong> Supabase (database), Vercel (hosting), Google (authentication), Anthropic (AI generation), Replicate (image generation), Resend (email), PostHog and Plausible (analytics). Each provider is bound by data processing agreements.</li>
                <li><strong style={{ color: "#18151f" }}>Legal requirements:</strong> if required by law, court order, or governmental authority</li>
                <li><strong style={{ color: "#18151f" }}>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, with notice to you</li>
                <li><strong style={{ color: "#18151f" }}>Protection of rights:</strong> to protect the safety, rights, or property of DayOne, our users, or the public</li>
              </ul>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>6. Data Retention</h2>
              <p>
                We retain your personal data for as long as your account is active or as needed to provide the Service. You may request deletion of your account and associated data at any time by contacting{" "}
                <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a>.
                We will delete your data within 30 days of receiving a verified request, except where retention is required by law.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>7. Your Rights</h2>
              <p className="mb-3">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong style={{ color: "#18151f" }}>Access:</strong> request a copy of the personal data we hold about you</li>
                <li><strong style={{ color: "#18151f" }}>Correction:</strong> request correction of inaccurate or incomplete data</li>
                <li><strong style={{ color: "#18151f" }}>Deletion:</strong> request deletion of your personal data ("right to be forgotten")</li>
                <li><strong style={{ color: "#18151f" }}>Portability:</strong> receive your data in a structured, machine-readable format</li>
                <li><strong style={{ color: "#18151f" }}>Restriction:</strong> request restriction of processing in certain circumstances</li>
                <li><strong style={{ color: "#18151f" }}>Objection:</strong> object to processing based on legitimate interests</li>
                <li><strong style={{ color: "#18151f" }}>Withdrawal of consent:</strong> withdraw consent at any time where processing is based on consent</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, contact{" "}
                <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a>.
                We will respond within 30 days.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>8. Cookies</h2>
              <p>
                We use essential cookies required for authentication and session management. On your first visit, we display a cookie banner where you can accept or decline non-essential tracking. If you decline, neither PostHog nor Plausible will load or collect any data. If you accept, we use PostHog for product analytics and Plausible for privacy-friendly pageview tracking (Plausible is cookieless and does not track you across websites). You can update your choice at any time by clearing your browser storage for usedayone.net.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>9. Children's Privacy</h2>
              <p>
                DayOne is not directed at children under 16. We do not knowingly collect personal data from children under 16. Contact{" "}
                <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a>{" "}
                if you believe we have inadvertently collected such data.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>10. International Data Transfers</h2>
              <p>
                DayOne operates from Italy and uses service providers based in the United States and other countries. When we transfer data outside the EEA, we ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>11. Security</h2>
              <p>
                We implement industry-standard security measures including encryption in transit (TLS), row-level security on our database, and secure authentication via OAuth. No method of transmission over the internet is 100% secure.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of material changes by email at least 14 days before the change takes effect.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>13. Contact Us</h2>
              <p>
                DayOne |{" "}
                <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a>{" "}
                | usedayone.net
              </p>
              <p className="mt-1" style={{ color: "#71717a" }}>
                Stone Sargent, Founder | Florence, Italy
              </p>
            </section>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
